import { GraphQLResult, generateClient } from "aws-amplify/api";
import { FieldInstanceForResolver, Image } from "db/definitions";
import { bulkRetrieveFieldInstancesByFormGroup } from "db/queries/fieldInstanceQueries";
import { fieldInstancesByFormGroup } from "graphql/queries";
import { recordSentry } from "utils/recordSentry";

interface QueryResponse {
	fieldInstancesByFormGroup: {
		items: FieldInstanceForResolver[];
		nextToken?: string;
	};
}

/**
 * Given the formGroup id and whether or not we're online, retrieves the fieldInstances and returns them
 * @author Eric Webb <ewebb@factorearth.com>
 * @param id The formGroup id we want to fetch the fieldInstances by
 * @param online Whether or not we're online
 * @returns The fieldInstances we retrieved
 */
export async function batchGetFieldInstancesByFormGroup(id: string, online: boolean): Promise<FieldInstanceForResolver[]> {
	const fieldInstances: FieldInstanceForResolver[] = [];
	try {
		if (online) {
			let token;
			while (token !== null) {
				const variables = token ? { formGroup: id, nextToken: token } : { formGroup: id };
				const client = generateClient({ authMode: "userPool" });
				const result = (await client.graphql({
					query: fieldInstancesByFormGroup,
					authMode: "userPool",
					variables: variables,
				})) as GraphQLResult<QueryResponse>;
				const fieldInstancesFromResponse = result.data.fieldInstancesByFormGroup.items;

				for (const field of fieldInstancesFromResponse) {
					fieldInstances.push(field);
				}
				token = result.data.fieldInstancesByFormGroup.nextToken || null;
			}
			return fieldInstances;
		} else {
			// Since this is offline, the fieldInstances might have "touched" on them. I think this is okay, not because the resolver accepts it, but because this is called in the
			// download json logic only. This would mean that their json shows us what fields have been touched. The uploads parse everything so this should just help us? Otherwise
			// we need to map the fieldInstances with the createFieldForBatchResolver function -Eric
			const offlineFieldInstances = await bulkRetrieveFieldInstancesByFormGroup(id);
			return offlineFieldInstances;
		}
	} catch (err) {
		recordSentry({ error: err, msg: `ERROR GETTING FIELD INSTANCES ${online ? "ONLINE" : "OFFLINE"}` });
	}
	return fieldInstances;
}
